/* eslint-disable jsx-a11y/no-onchange */
import * as React from 'react';
import { FC } from 'react';

import { useTheme } from 'styled-components';

import { useIdCounter } from '../../hooks';
import { getRequiredLabel } from '../../utils/internal';
import { Icon } from '../icon';
import { InputMessage } from '../input-message';

import { IconWrapper, Label, SelectWrapper, StyledSelect, Wrapper } from './select.styled';
import { ISelectProps } from './types';

/**
 * Select is a drop-down list to select value from options.
 */
const Select: FC<ISelectProps> = ({
  onChange,
  options,
  value,
  label,
  errorMessage = '',
  hintMessage = '',
  disabled,
  id: inputId,
  name,
  required,
  autoComplete,
  'data-testid': dataTestId,
}) => {
  const { token } = useTheme();
  // Add an empty select option with the label name
  const selectOptions = [{ value: '', label }, ...options];

  const hasValue = !!value;

  // The error message should be prioritize when there is an error
  const inputMessage = errorMessage || hintMessage;
  // Add an '*' if the input is required
  const inputLabel = getRequiredLabel(label, required);
  // HC the icon size for now
  const ICON_SIZE = token('width-icon');

  // Use the fallback of useIdCounter if no id is passed as props
  const defaultInputId = useIdCounter('select-component');
  const uniqueId = inputId ?? defaultInputId;
  // Creating select message ID for aria-describedby
  const selectMessageId = useIdCounter('select-message');

  const hasError = !!errorMessage;

  return (
    <Wrapper>
      <SelectWrapper>
        <Label
          id={`label-for-${uniqueId}`}
          htmlFor={uniqueId}
          data-testid="select-label"
          hasValue={hasValue}
          hasError={hasError}
          disabled={disabled}
        >
          {inputLabel}
        </Label>

        <StyledSelect
          name={name}
          id={uniqueId}
          required={required}
          onChange={onChange}
          value={value}
          disabled={disabled}
          hasError={hasError}
          data-testid={dataTestId ?? 'select-component'}
          aria-describedby={selectMessageId}
          autoComplete={autoComplete}
        >
          {selectOptions.map(
            ({ value: optionValue, label: optionLabel, disabled: optionDisabled }) => (
              <option
                value={optionValue}
                key={optionValue}
                disabled={!optionValue || optionDisabled}
              >
                {optionLabel}
              </option>
            ),
          )}
        </StyledSelect>

        {!disabled && (
          <IconWrapper>
            <Icon
              icon="select"
              color="icon-default"
              width={ICON_SIZE}
              height={ICON_SIZE}
              aria-hidden
              data-testid="select-icon"
            />
          </IconWrapper>
        )}
      </SelectWrapper>

      <InputMessage id={selectMessageId} hasError={hasError} disabled={disabled}>
        {inputMessage}
      </InputMessage>
    </Wrapper>
  );
};

export default Select;
